import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Clock, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SprintScene } from '@/types/gameSpec';
import { shuffle, SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

/**
 * Timed rapid-fire MCQ. "Correct" if the learner reaches a 70% accuracy
 * threshold before the timer runs out (or finishes all questions).
 */
export const SprintSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<SprintScene>) => {
  const order = useMemo(
    () => shuffle(scene.questions.map((_, i) => i)),
    [scene.id],
  );
  const [qPos, setQPos] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [timeLeft, setTimeLeft] = useState(scene.duration_seconds);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) return;
    if (timeLeft <= 0) {
      finish();
      return;
    }
    const t = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, done]);

  const finish = () => {
    if (done) return;
    setDone(true);
    const total = correct + wrong;
    const accuracy = total > 0 ? correct / total : 0;
    const reached = total >= Math.min(3, scene.questions.length);
    const ok = reached && accuracy >= 0.7;
    setTimeout(
      () => onAnswer(ok, { correct, wrong, accuracy }),
      600,
    );
  };

  const pick = (origQIdx: number, optIdx: number) => {
    if (done) return;
    const q = scene.questions[origQIdx];
    if (optIdx === q.correct_index) setCorrect((c) => c + 1);
    else setWrong((w) => w + 1);

    if (qPos + 1 >= order.length) {
      finish();
    } else {
      setQPos(qPos + 1);
    }
  };

  const currentQOrigIdx = order[qPos];
  const q = scene.questions[currentQOrigIdx];
  const timePct = (timeLeft / scene.duration_seconds) * 100;

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {SCENE_LABEL.sprint}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1">
              <Trophy className="h-4 w-4" /> {correct}
            </span>
            <span className="flex items-center gap-1 tabular-nums">
              <Clock className="h-4 w-4" /> {timeLeft}s
            </span>
          </div>
        </div>

        <Progress
          value={timePct}
          className={cn(
            'h-2',
            timePct < 25 && '[&>div]:bg-destructive',
          )}
        />

        <p className="text-lg font-medium">{scene.prompt}</p>

        {!done && q && (
          <>
            <p className="text-xl font-semibold">{q.text}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {q.options.map((opt, i) => (
                <Button
                  key={i}
                  variant="outline"
                  onClick={() => pick(currentQOrigIdx, i)}
                  className="h-auto py-3 justify-start whitespace-normal text-left"
                >
                  {opt}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Question {qPos + 1} / {order.length}
            </p>
          </>
        )}

        {done && (
          <p className="font-medium">
            Sprint over — {correct} correct, {wrong} wrong.
          </p>
        )}
      </CardContent>
    </Card>
  );
};
